import React, { useMemo, useState } from 'react';

import useSittingStore from '../../stores/sittingStore';
import ProcedureNoteDialog from './ProcedureNoteDialog';

// Stable empty fallback so the Zustand selector returns the same
// reference on every render when the thread isn't hydrated yet.
const EMPTY_NOTES = Object.freeze([]);

function formatStamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = (a, b) => (
      a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate()
    );
    const time = d.toLocaleTimeString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
    if (sameDay(d, today)) return time;
    const date = d.toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short',
    });
    return `${date} · ${time}`;
  } catch {
    return '';
  }
}

function truncate(text, max = 120) {
  if (!text) return '';
  const s = String(text);
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

function roleLabel(role) {
  if (!role) return '';
  const r = String(role);
  return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
}

/**
 * ProcedureNoteThreadPreview — inline read-surface for the GSI9-backed
 * procedure_notes thread, shown under each SittingCard on the doctor
 * visit screen.
 *
 * The cache is hydrated eagerly by VisitProceduresPanel via
 * `useSittingStore().fetchVisitNotes(visit_id)` (one Query for the
 * whole visit). WS event `SITTING_NOTE_ADDED` keeps it live.
 *
 * Renders nothing when the sitting has zero notes — the dialog is
 * still reachable from the SittingCard's "📝 Note" action.
 */
export default function ProcedureNoteThreadPreview({ sitting, viewer }) {
  const sittingId = sitting?.sitting_id;
  const notes = useSittingStore(
    (s) => (sittingId ? (s.notesBySitting?.[sittingId] || EMPTY_NOTES) : EMPTY_NOTES),
  );
  const [open, setOpen] = useState(false);

  const recent = useMemo(() => notes.slice(-2), [notes]);
  const total = notes.length;

  if (total === 0) return null;

  return (
    <>
      <div className="ml-2 pl-3 border-l-2 border-indigo-300 dark:border-indigo-700">
        <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300 mb-1">
          💬 Procedure notes ({total})
        </div>
        <div className="space-y-1">
          {recent.map((n) => {
            const author = n.author || {};
            return (
              <div
                key={n.note_id}
                className="text-sm text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/60 rounded-md px-3 py-1.5 border border-slate-300 dark:border-slate-700"
              >
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">
                  {author.name || 'Unknown'}
                  {author.role ? ` · ${roleLabel(author.role)}` : ''}
                  {n.created_at ? ` · ${formatStamp(n.created_at)}` : ''}
                </div>
                <div className="whitespace-pre-wrap break-words">
                  {truncate(n.body)}
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:underline"
        >
          View all {total} note{total === 1 ? '' : 's'} →
        </button>
      </div>
      {open && (
        <ProcedureNoteDialog
          sitting={sitting}
          viewer={viewer}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
